
import React, { useMemo } from 'react';
import { isSSR } from '@/lib/isSSR';
import { StaticMap } from './map/StaticMap';
import { ServerMapPlaceholder } from './map/server/ServerMapPlaceholder';
import { Dealer } from '@/types/dealer';

// Define the props interface
interface DealerLocationMapProps {
  latitude: number;
  longitude: number;
  name: string;
  address: string;
  height?: string;
  width?: string;
  zoom?: number;
  mapId?: string; // Optional map ID for consistent identification
}

/**
 * A component that renders a map showing a dealer's location
 * Uses different implementations for server-side rendering and client-side rendering
 * Ensures consistent map IDs between server and client for proper hydration
 */
const DealerLocationMap: React.FC<DealerLocationMapProps> = (props) => {
  // Generate a consistent mapId based on dealer coordinates if not provided
  const mapId = useMemo(() => {
    if (props.mapId) return props.mapId;
    
    // Create a deterministic ID based on coordinates to ensure consistency between SSR and client
    const coordString = `${props.latitude.toFixed(6)}-${props.longitude.toFixed(6)}`;
    return `map-${coordString.replace(/\./g, '_')}`;
  }, [props.latitude, props.longitude, props.mapId]);
  
  // Create a dealer object from props for the marker
  // Memoized with a stable reference to prevent unnecessary re-renders
  const dealer = useMemo((): Dealer => {
    const dealerObj = {
      id: mapId, // Use mapId as unique identifier
      name: props.name,
      address: props.address,
      latitude: props.latitude,
      longitude: props.longitude,
      rating: 0, // Default values for required fields
      is_verified: false,
      activated: false,
      isFavorite: false,
      // Optional fields can be undefined
      buySpread: undefined,
      image_url: undefined,
      postal_code: undefined,
      license: undefined,
      opening_hours: undefined,
      phone: undefined,
      website: undefined,
      city: undefined,
      email: undefined,
      distance: undefined
    };
    
    // Freeze the object to prevent accidental mutations that could cause re-renders
    return Object.freeze(dealerObj) as Dealer;
  }, [mapId, props.name, props.address, props.latitude, props.longitude]);
  
  // Memoize the dealers array to prevent recreating it on every render
  const dealers = useMemo(() => [dealer], [dealer]);
  
  // Use the ServerMapPlaceholder for SSR to avoid loading map libraries on the server
  if (isSSR()) {
    return <ServerMapPlaceholder {...props} mapId={mapId} />;
  }
  
  // Use the client-side StaticMap for browser rendering
  // Pass the created dealer object in the dealers array
  return <StaticMap 
    center={[props.latitude, props.longitude]}
    zoom={props.zoom || 13}
    dealers={dealers} 
    height={props.height}
    width={props.width}
    mapId={mapId}
  />;
};

export default DealerLocationMap;
export { DealerLocationMap };
